import React, { useState, useEffect } from "react";
import { Grid, TextField, Button, Container } from "@mui/material";
import { useSelector } from "react-redux";
import Header from "components/Header";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import forageImage from "../../assets/forage.png";
import api from "services/api";

const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Convertir une date de yyyy-MM-dd en dd-MM-yyyy
const formatDateToDDMMYYYY = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Convertir une date de dd-MM-yyyy en yyyy-MM-dd
const convertToYYYYMMDD = (dateString) => {
  const [day, month, year] = dateString.split("-");
  return `${year}-${month}-${day}`;
};

// Récupérer le premier jour du mois en cours en dd-MM-yyyy
const getFirstDayOfCurrentMonth = () => {
  const currentDate = new Date();
  currentDate.setUTCDate(1); // 1er jour du mois actuel
  const firstDay = currentDate.toISOString().split("T")[0];
  return formatDateToDDMMYYYY(firstDay);
};

const getLastDayOfCurrentMonth = () => {
  const currentDate = new Date();
  const year = currentDate.getUTCFullYear();
  const month = currentDate.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  const lastDayFormatted = lastDay.toISOString().split("T")[0];
  return formatDateToDDMMYYYY(lastDayFormatted);
};

const DonneesTechniques = () => {
  const mode = useSelector((state) => state.global.mode);
  const [startDate, setStartDate] = useState(getFirstDayOfCurrentMonth());
  const [endDate, setEndDate] = useState(getLastDayOfCurrentMonth());

  const [formData, setFormData] = useState({
    selectedDate: getCurrentDate(),
    indexSpdm: 0,
    indexSpfm: 0,
    nombreMp: 0,
    nombreMd: 0,
    stockDisponible: 0,
    perteReseau: 0,
    observations: "",
  });

  useEffect(() => {
    const fetchConsommationTotal = async () => {
      if (!startDate || !endDate) {
        toast.error("Veuillez sélectionner une plage de dates.");
        return;
      }

      try {
        const response = await api.get(
          `/api/consommations/totalMensuel?startDate=${startDate}&endDate=${endDate}`
        );

        const data = response.data;

        console.log("totalMensuel", data);

        setFormData((prevData) => ({
          ...prevData,
          nombreMd: Number(data?.totalConsommation || 0),
        }));
      } catch (error) {
        console.error(error);

        toast.error(
          "Erreur lors de la récupération de la consommation totale. Veuillez réessayer."
        );
      }
    };

    fetchConsommationTotal();
  }, [startDate, endDate]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      nombreMp: prevData.indexSpdm - prevData.indexSpfm,
    }));
  }, [formData.indexSpdm, formData.indexSpfm]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      perteReseau:
        prevData.nombreMp - prevData.nombreMd - prevData.stockDisponible,
    }));
  }, [formData.nombreMp, formData.nombreMd, formData.stockDisponible]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]:
        name === "selectedDate" || name === "observations"
          ? value
          : value === ""
            ? ""
            : Number(value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await api.post("/api/donnees", formData);

      console.log("Form submitted:", response.data);

      toast.success("Formulaire soumis avec succès.");

      setFormData({
        selectedDate: getCurrentDate(),
        indexSpdm: 0,
        indexSpfm: 0,
        nombreMp: 0,
        nombreMd: 0,
        stockDisponible: 0,
        perteReseau: 0,
        observations: "",
      });
    } catch (error) {
      console.error("Erreur lors de la soumission du formulaire!", error);

      toast.error(
        "Erreur lors de la soumission du formulaire. Veuillez réessayer."
      );
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, ml: 3 }}>
      <Header
        title=" Ajouter une donnée technique"
        subtitle="La valeur 0 est rempli par défaut"
        sx={{ mb: 5 }}
      />
      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Grid item xs={8}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Date"
                  type="date"
                  name="selectedDate"
                  value={formData.selectedDate}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              {[
                "indexSpdm",
                "indexSpfm",
                "nombreMp",
                "nombreMd",
                "stockDisponible",
                "perteReseau",
              ].map((field) => (
                <Grid item xs={6} key={field}>
                  <TextField
                    label={field.replace(/([A-Z])/g, " $1").trim()}
                    type="number"
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    fullWidth
                    InputProps={
                      field === "nombreMd" || field === "perteReseau"
                        ? { readOnly: true }
                        : {}
                    }
                    disabled={field === "nombreMd" || field === "perteReseau"}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  label="Observations"
                  name="observations"
                  value={formData.observations}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color={mode === "dark" ? "secondary" : "primary"}
                  size="large"
                  sx={{
                    width: "100%",
                  }}
                >
                  Ajouter
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Grid item xs={4} container alignItems="center" justifyContent="center">
          <TextField
            id="start-date"
            label="Période de début"
            type="date"
            value={convertToYYYYMMDD(startDate)}
            onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="end-date"
            label="Période de fin"
            type="date"
            value={convertToYYYYMMDD(endDate)}
            onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <img
            src={forageImage}
            alt="Forage"
            style={{
              width: "100%",
              maxWidth: "200px",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default DonneesTechniques;

